import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  Calendar,
  Download,
  Filter,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Users,
  Activity,
  Clock,
  MapPin,
  AlertCircle,
  CheckCircle,
  XCircle,
  Search,
  Eye,
  MoreVertical
} from 'lucide-react';
import analyticsService from '../../services/analyticsService';

const AdminAnalyticsDashboard = () => {
  // State for analytics data
  const [dashboardData, setDashboardData] = useState(null);
  const [bookingTrends, setBookingTrends] = useState([]);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [geographicData, setGeographicData] = useState(null);

  // State for UI controls
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportModal, setShowExportModal] = useState(false);

  // Load all analytics data
  const loadAnalyticsData = useCallback(async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const filters = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      // Load all analytics data in parallel
      const [
        overviewResponse,
        trendsResponse,
        paymentsResponse,
        performanceResponse,
        geoResponse
      ] = await Promise.allSettled([
        analyticsService.getDashboardOverview(filters),
        analyticsService.getBookingTrends({ ...filters, granularity: 'daily' }),
        analyticsService.getPaymentAnalytics(filters),
        analyticsService.getAmbulancePerformance(filters),
        analyticsService.getGeographicAnalytics(filters)
      ]);

      // Process responses
      if (overviewResponse.status === 'fulfilled') {
        setDashboardData(overviewResponse.value.data);
      }
      if (trendsResponse.status === 'fulfilled') {
        setBookingTrends(trendsResponse.value.data?.trends || []);
      }
      if (paymentsResponse.status === 'fulfilled') {
        setPaymentAnalytics(paymentsResponse.value.data);
      }
      if (performanceResponse.status === 'fulfilled') {
        setPerformanceMetrics(performanceResponse.value.data);
      }
      if (geoResponse.status === 'fulfilled') {
        setGeographicData(geoResponse.value.data);
      }

      // Check for any errors
      const errors = [overviewResponse, trendsResponse, paymentsResponse, performanceResponse, geoResponse]
        .filter(response => response.status === 'rejected')
        .map(response => response.reason.message);

      if (errors.length > 0) {
        console.warn('Some analytics data failed to load:', errors);
        setError(`Failed to load some data: ${errors.join(', ')}`);
      }

    } catch (err) {
      console.error('Analytics loading error:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange]);

  // Load data on component mount and date range changes
  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Handle date range change
  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle refresh
  const handleRefresh = () => {
    loadAnalyticsData(true);
  };

  // Handle export
  const handleExport = async (exportType, format) => {
    try {
      const exportOptions = {
        type: exportType,
        format: format,
        filters: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      };

      const response = await analyticsService.exportAnalyticsData(exportOptions);

      if (response.success) {
        // If immediate download URL is provided
        if (response.data.downloadUrl) {
          window.open(response.data.downloadUrl, '_blank');
        } else {
          // Show success message - file will be ready for download soon
          alert('Export started! You will be notified when the file is ready for download.');
        }
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed. Please try again.');
    }
    setShowExportModal(false);
  };

  // Color schemes for charts
  const colors = {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    secondary: '#6b7280'
  };

  const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Admin Analytics</h1>
              {refreshing && (
                <RefreshCw className="h-4 w-4 animate-spin ml-3 text-blue-600" />
              )}
            </div>

            <div className="flex items-center space-x-4">
              {/* Date Range Picker */}
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Actions */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>

              <button
                onClick={() => setShowExportModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 m-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Warning</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: Activity },
              { id: 'bookings', name: 'Bookings', icon: FileText },
              { id: 'payments', name: 'Payments', icon: DollarSign },
              { id: 'performance', name: 'Performance', icon: TrendingUp },
              { id: 'geographic', name: 'Geographic', icon: MapPin }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.totalBookings?.toLocaleString() || '0'}
                    </p>
                    {dashboardData.bookingGrowth && (
                      <div className="flex items-center">
                        {dashboardData.bookingGrowth > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-sm ${dashboardData.bookingGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.abs(dashboardData.bookingGrowth)}% vs last period
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₹{dashboardData.totalRevenue?.toLocaleString() || '0'}
                    </p>
                    {dashboardData.revenueGrowth && (
                      <div className="flex items-center">
                        {dashboardData.revenueGrowth > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-sm ${dashboardData.revenueGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.abs(dashboardData.revenueGrowth)}% vs last period
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Users className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Users</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.activeUsers?.toLocaleString() || '0'}
                    </p>
                    {dashboardData.userGrowth && (
                      <div className="flex items-center">
                        {dashboardData.userGrowth > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-sm ${dashboardData.userGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.abs(dashboardData.userGrowth)}% vs last period
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.avgResponseTime || '0'} min
                    </p>
                    {dashboardData.responseTimeChange && (
                      <div className="flex items-center">
                        {dashboardData.responseTimeChange > 0 ? (
                          <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                        ) : (
                          <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                        )}
                        <span className={`text-sm ${dashboardData.responseTimeChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {Math.abs(dashboardData.responseTimeChange)} min vs last period
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Booking Trends Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Booking Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={bookingTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="bookings" stroke={colors.primary} fill={colors.primary} fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Status Distribution */}
              {dashboardData.statusDistribution && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Booking Status Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={dashboardData.statusDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {dashboardData.statusDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Detailed Booking Analytics</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Comprehensive booking data will be displayed here including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>Booking logs with search and filtering</li>
                  <li>Status change timeline</li>
                  <li>Peak hours analysis</li>
                  <li>Ambulance type distribution</li>
                  <li>Hospital-wise booking breakdown</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && paymentAnalytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Successful Payments</p>
                    <p className="text-2xl font-bold text-green-600">
                      ₹{paymentAnalytics.successfulPayments?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Failed Payments</p>
                    <p className="text-2xl font-bold text-red-600">
                      ₹{paymentAnalytics.failedPayments?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Success Rate</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {paymentAnalytics.successRate || '0'}%
                    </p>
                  </div>
                  <Activity className="h-8 w-8 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Payment trends chart would go here */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Analytics Details</h3>
              <p className="text-gray-600">
                Detailed payment analytics including transaction logs, failure analysis, and revenue trends.
              </p>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Analytics</h3>
              <p className="text-gray-600">
                Performance metrics for ambulances, drivers, and system efficiency will be displayed here.
              </p>
            </div>
          </div>
        )}

        {/* Geographic Tab */}
        {activeTab === 'geographic' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Geographic Analytics</h3>
              <p className="text-gray-600">
                Geographic distribution of bookings, service coverage areas, and hotspot analysis.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Export Analytics Data</h3>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Type</label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="bookings">Booking Analytics</option>
                    <option value="payments">Payment Analytics</option>
                    <option value="performance">Performance Metrics</option>
                    <option value="geographic">Geographic Data</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="csv">CSV</option>
                    <option value="excel">Excel</option>
                    <option value="pdf">PDF Report</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExport('bookings', 'csv')}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalyticsDashboard;